/**
 * LibraryOverview — displays library metadata, member list, and settings
 * when no page is selected (i.e., user clicked the library name in the sidebar).
 */

import React, { useCallback, useEffect, useState } from 'react';
import { usePagesContext } from '../../state/pages-context';
import * as pagesApi from '../../api/pages-api';
import type { Library, LibraryMember, UserInfo } from '../../api/pages-api';
import { Badge } from '../../components/ui/badge';
import { LibraryShareDialog } from './LibraryShareDialog';
import { WatchButton } from '../../components/WatchButton';
import { Button } from '../../components/ui/button';
import { getTokens } from '../../services/auth-client';

interface LibraryOverviewProps {
  readonly libraryId: string;
}

export function LibraryOverview({ libraryId }: LibraryOverviewProps): React.ReactElement {
  const { state, fetchLibraries, selectLibrary } = usePagesContext();
  const [library, setLibrary] = useState<Library | null>(null);
  const [members, setMembers] = useState<LibraryMember[]>([]);
  const [users, setUsers] = useState<Map<string, string>>(new Map());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const fetchDetails = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [lib, membersRes, usersRes] = await Promise.all([
        pagesApi.getLibrary(libraryId),
        pagesApi.listMembers(libraryId),
        pagesApi.listUsers(),
      ]);
      setLibrary(lib);
      setMembers(membersRes.data ?? []);
      // Build userId → displayName map
      const userList: UserInfo[] = usersRes.data ?? [];
      const userMap = new Map<string, string>();
      for (const u of userList) {
        userMap.set(u.userId, u.displayName);
      }
      setUsers(userMap);
    } catch (err: unknown) {
      const msg = err && typeof err === 'object' && 'message' in err
        ? (err as { message: string }).message
        : 'Failed to load library details';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [libraryId]);

  useEffect(() => {
    fetchDetails();
  }, [fetchDetails]);

  const handleDeleteLibrary = useCallback(async () => {
    setDeleting(true);
    try {
      await pagesApi.deleteLibrary(libraryId);
      selectLibrary(null as unknown as string);
      fetchLibraries();
    } catch {
      setDeleting(false);
    }
  }, [libraryId, fetchLibraries, selectLibrary]);

  /** Resolve a userId to a display name, falling back to a loading placeholder. */
  const resolveUserName = useCallback((userId: string): string => {
    return users.get(userId) ?? (loading ? '…' : userId);
  }, [users, loading]);

  // Fall back to context state if API hasn't returned yet
  const displayLib = library ?? state.libraries.find((l) => l.libraryId === libraryId) ?? null;

  if (loading && !displayLib) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-muted-foreground" role="status" aria-live="polite">Loading library...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-destructive" role="alert">{error}</p>
      </div>
    );
  }

  if (!displayLib) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-muted-foreground">Library not found.</p>
      </div>
    );
  }

  const pageCount = state.pageTree.length;

  // Determine if current user is an admin of this library
  const currentUserId = (() => {
    const tokens = getTokens();
    if (!tokens?.accessToken) return '';
    try {
      const payload = tokens.accessToken.split('.')[1];
      const decoded = JSON.parse(atob(payload!));
      return decoded.sub ?? '';
    } catch { return ''; }
  })();
  const isAdmin = members.some((m) => m.userId === currentUserId && m.role === 'admin')
    || (displayLib && displayLib.createdBy === currentUserId);

  return (
    <div className="max-w-2xl mx-auto space-y-8">
      {/* Header */}
      <div>
        <div className="flex items-center gap-3 mb-2">
          <h1 className="text-2xl font-semibold">{displayLib.name}</h1>
          <Badge variant={displayLib.visibility !== 'private' ? 'default' : 'secondary'}>
            {displayLib.visibility === 'public-edit' ? 'Public (can edit)' : displayLib.visibility === 'public-read' || displayLib.visibility === 'public' ? 'Public (view only)' : 'Private'}
          </Badge>
          <LibraryShareDialog
            libraryId={displayLib.libraryId}
            libraryName={displayLib.name}
            currentVisibility={displayLib.visibility}
          />
          <WatchButton resourceType="library" resourceId={displayLib.libraryId} />
        </div>
        {displayLib.description && (
          <p className="text-muted-foreground">{displayLib.description}</p>
        )}
        {!displayLib.description && (
          <p className="text-muted-foreground italic">No description set.</p>
        )}
      </div>

      {/* Quick stats */}
      <section aria-label="Library statistics">
        <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wide mb-3">Overview</h2>
        <div className="grid grid-cols-3 gap-4">
          <div className="bg-muted/40 rounded-md p-4 text-center">
            <p className="text-2xl font-semibold">{pageCount}</p>
            <p className="text-xs text-muted-foreground mt-1">{pageCount === 1 ? 'Page' : 'Pages'}</p>
          </div>
          <div className="bg-muted/40 rounded-md p-4 text-center">
            <p className="text-2xl font-semibold">{members.length}</p>
            <p className="text-xs text-muted-foreground mt-1">{members.length === 1 ? 'Member' : 'Members'}</p>
          </div>
          <div className="bg-muted/40 rounded-md p-4 text-center">
            <p className="text-2xl font-semibold capitalize">{displayLib.visibility}</p>
            <p className="text-xs text-muted-foreground mt-1">Visibility</p>
          </div>
        </div>
      </section>

      {/* Members */}
      <section aria-label="Library members">
        <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wide mb-3">Members</h2>
        {members.length === 0 && (
          <p className="text-sm text-muted-foreground italic">No members added yet.</p>
        )}
        {members.length > 0 && (
          <div className="border rounded-md divide-y">
            {members.map((member) => (
              <div key={member.userId} className="flex items-center justify-between px-4 py-3">
                <span className="text-sm font-medium">{resolveUserName(member.userId)}</span>
                <Badge variant="outline" className="text-xs capitalize">{member.role}</Badge>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Metadata */}
      <section aria-label="Library details">
        <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wide mb-3">Details</h2>
        <dl className="grid grid-cols-[auto_1fr] gap-x-6 gap-y-2 text-sm">
          <dt className="text-muted-foreground">Created by</dt>
          <dd>{resolveUserName(displayLib.createdBy)}</dd>
          <dt className="text-muted-foreground">Created</dt>
          <dd>{new Date(displayLib.createdAt).toLocaleDateString()}</dd>
          <dt className="text-muted-foreground">Last updated</dt>
          <dd>{new Date(displayLib.updatedAt).toLocaleDateString()}</dd>
          <dt className="text-muted-foreground">Library ID</dt>
          <dd className="font-mono text-xs text-muted-foreground">{displayLib.libraryId}</dd>
        </dl>
      </section>

      {/* Danger Zone — only for library admins */}
      {isAdmin && (
        <section aria-label="Danger zone" className="border border-destructive/30 rounded-md p-4">
          <h2 className="text-sm font-medium text-destructive uppercase tracking-wide mb-2">Danger Zone</h2>
          {!confirmingDelete ? (
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium">Delete this library</p>
                <p className="text-xs text-muted-foreground">Permanently delete "{displayLib.name}" and all its pages.</p>
              </div>
              <Button
                variant="destructive"
                size="sm"
                className="text-xs"
                onClick={() => setConfirmingDelete(true)}
              >
                Delete Library
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              <p className="text-sm text-destructive">
                This will permanently delete <strong>{displayLib.name}</strong> and all {pageCount} page{pageCount !== 1 ? 's' : ''} within it. This cannot be undone.
              </p>
              <div className="flex gap-2">
                <Button
                  variant="destructive"
                  size="sm"
                  className="text-xs"
                  onClick={handleDeleteLibrary}
                  disabled={deleting}
                >
                  {deleting ? 'Deleting...' : 'Yes, delete permanently'}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-xs"
                  onClick={() => setConfirmingDelete(false)}
                  disabled={deleting}
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </section>
      )}
    </div>
  );
}
